import { useEffect, useRef, useState } from "react";
import { motion, animate } from "framer-motion";
import { styled } from "@mui/material/styles";

function AnimatedCardValue({
  value,
  duration = 0.25,
  format = false, // Add commas (1,234)
  decimals = 0, // Number of decimal places
  suffix = "", // Add suffix like "%"
  animateDown = false // Whether to animate decreases
}) {
  const numericValue = parseFloat(value) || 0;
  const [displayValue, setDisplayValue] = useState(numericValue);
  const previousValueRef = useRef(numericValue);
  const animationRef = useRef(null);

  useEffect(() => {
    const prevValue = previousValueRef.current;

    // Skip if no change
    if (numericValue === prevValue) return;

    // Cancel any existing animation
    if (animationRef.current) {
      animationRef.current.stop();
    }

    // Conditionally animate based on direction
    const shouldAnimate = animateDown || numericValue > prevValue;

    if (shouldAnimate) {
      animationRef.current = animate(prevValue, numericValue, {
        duration,
        onUpdate: latest => {
          setDisplayValue(latest);
        },
        onComplete: () => {
          setDisplayValue(numericValue);
          previousValueRef.current = numericValue;
        }
      });
    } else {
      setDisplayValue(numericValue);
      previousValueRef.current = numericValue;
    }

    return () => {
      if (animationRef.current) {
        animationRef.current.stop();
      }
    };
  }, [numericValue, duration, animateDown]);

  const formatValue = val => {
    // Round based on decimal places
    const rounded = decimals > 0 ? val.toFixed(decimals) : Math.round(val);

    const formatted =
      format && decimals === 0 ? parseInt(rounded).toLocaleString() : rounded;

    return `${formatted}${suffix}`;
  };

  return (
    <StyledCardValue as={motion.div}>
      {formatValue(displayValue)}
    </StyledCardValue>
  );
}

const StyledCardValue = styled("div")(({ theme }) => ({
  color: "var(--teleforce-50)",
  fontSize: "1.7rem",
  lineHeight: 1,
  fontFamily: "Roboto Mono, sans-serif",

  [theme.breakpoints.up("md")]: {
    fontSize: "1.8rem"
  }
}));

export default AnimatedCardValue;
